import { Dialog } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getBidderGroups, IBidderGroup, updateBidderGroups } from '~/src/dash/api/bidder-group';
import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { GroupSettingsDialog } from '~/src/dash/components/group-settings-dialog';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { activeQuickFilterOptions } from '~/src/dash/components/quick-filter-dropdown';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractSearchableColumnKeys,
  extractVisibleColumns,
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelectorAndTableExpand } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelectorAndTableExpand';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  RouteBidderSettingsExisting,
} from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { obfuscateBidderGroupName } from '~/src/dash/utils/obfuscate';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  formatBidderGroupsResponseIntoLVTRows,
  IBidderGroupsListViewConfig,
} from '~/src/dash/views/bidders/Bidders/tabs/BidderGroupsListView/config';
import { useBiddersListStore } from '~/src/dash/views/bidders/contexts';
import { getBiddersList } from '~/src/dash/views/common/dashboards-consts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

enum ESelectedModal {
  GroupEditor = 1,
  BiddersList,
}

const extractAllBidderGroupLabels = (groups: IBidderGroup[]) => {
  const allLabels: IBidderGroupLabel[] = [];
  const groupLabels = new Set();
  for (const group of groups) {
    if (group.BidderGroupLabels && group.BidderGroupLabels.length) {
      for (const bidderGroupLabel of group.BidderGroupLabels) {
        if (!groupLabels.has(bidderGroupLabel.ID)) {
          groupLabels.add(bidderGroupLabel.ID);
          allLabels.push(bidderGroupLabel);
        }
      }
    }
  }
  return allLabels;
};

const queryFilteredData = (
  filteredData: IBidderGroup[],
  query: string,
  searchableColumnKeys: string[],
) => {
  let queriedRows = cloneDeep(filteredData);
  if (query !== '') {
    queriedRows = filteredData.filter((row) => {
      for (const key of searchableColumnKeys) {
        // @ts-ignore
        if (row[key].toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }
  return queriedRows;
};

export const BidderGroupsListView = () => {
  const { session, user } = useBaseStore();
  const { readAuthorization, writeAuthorization } = useBiddersListStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const { Bidders } = useBaseConfig();
  const cfg = Bidders.Groups as IBidderGroupsListViewConfig;
  const { cypressClassName, defaultState } = cfg;
  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<IBidderGroup[]>([]);
  const [queriedData, setQueriedData] = useState<IBidderGroup[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);
  const [allGroupLabels, setAllGroupLabels] = useState<IBidderGroupLabel[]>([]);
  // groups for batch update
  const [tempGroups, setTempGroups] = useState<IBidderGroup[]>([]);
  // loading
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  // search
  const [query, setQuery] = useState('');
  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(
    defaultState.isTableExpanded || false,
  );
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || 'Name');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  // bidders
  const [biddersList, setBiddersList] = useState<IMenuList>({});
  const [filteredBiddersList, setFilteredBiddersList] = useState<IMenuList>({});
  // modal states
  const [activeGroup, setActiveGroup] = useState<IBidderGroup | undefined>(undefined);
  const [activeModal, setActiveModal] = useState<ESelectedModal | undefined>(undefined);
  const [isGroupSettingsModalActive, setIsGroupSettingsModalActive] = useState<boolean>(false);
  const [isBiddersListModalActive, setIsBiddersListModalActive] = useState<boolean>(false);
  const [selectedBidderGroupLabels, setSelectedBidderGroupLabels] = useState<IBidderGroupLabel[]>(
    [],
  );
  // other consts
  const searchableColumnKeys = extractSearchableColumnKeys(cfg.table.columns);
  const isDemoUser = isDemoAccount(user.user.Username);

  useEffect(() => {
    // get bidders first before loading groups
    getBidders();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      // load group data only after storing the bidders list for reference in order to count only active bidders in bidder groups
      getData();
    }
  }, [biddersList]);

  // filter data via search query
  useEffect(() => {
    if (isMounted.current) {
      const queriedRows = queryFilteredData(responseData, query, searchableColumnKeys);
      const clonedData = cloneDeep(responseData);
      unstable_batchedUpdates(() => {
        setQueriedData(queriedRows);
        setTotalRowCount(queriedRows.length);
        setAllGroupLabels(extractAllBidderGroupLabels(responseData));
        setPage(cfg.isInitialized ? defaultState.page : 0);
        setLoadState(!isEmpty(queriedRows) ? LoadingStateHasData : LoadingStateNoData);
        setTempGroups(clonedData);
      });
    }
  }, [responseData, query]);

  // sort queriedData via column or sort bys
  useEffect(() => {
    if (isMounted.current) {
      if (!isEmpty(queriedData)) {
        setSortedRows(
          stableSort(rows, getComparator(order, orderBy, cfg.table.columns[orderBy].valueType)),
        );
      } else {
        setSortedRows([]);
      }
      cfg.defaultState.orderBy = orderBy;
      cfg.defaultState.order = order;
    }
  }, [queriedData, order, orderBy]);

  // once an activeGroup is selected, we correct the bidder list
  useEffect(() => {
    if (isMounted.current && activeGroup && !isEmpty(activeGroup)) {
      const clonedBiddersList = cloneDeep(biddersList);
      const selectedIDs = activeGroup.BidderIDs.split(',');
      selectedIDs.forEach((id) => {
        if (clonedBiddersList[id]) {
          clonedBiddersList[id].selected = true;
        }
      });
      setFilteredBiddersList(clonedBiddersList);
    }
  }, [activeGroup]);

  // once the filtered bidder list is stored, we open the correct modal
  useEffect(() => {
    if (isMounted.current && !!activeModal) {
      switch (activeModal) {
        case ESelectedModal.GroupEditor:
          setIsGroupSettingsModalActive(true);
          break;
        case ESelectedModal.BiddersList:
          setIsBiddersListModalActive(true);
        default:
          break;
      }
    }
  }, [filteredBiddersList]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      // @ts-ignore
      cfg.defaultState.activeColumnKeys = activeColumnKeys;
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  const getData = () => {
    if (readAuthorization.isAuthorized) {
      setLoadState(LoadingStateLoading);
      getBidderGroups({
        query: {
          publisher_id: session.publisher.ID,
        },
      })
        .then(({ bidderGroups }: { bidderGroups: IBidderGroup[] }) => {
          bidderGroups.forEach((group) => {
            // obfuscate if needed
            if (isDemoUser) {
              group.Name = obfuscateBidderGroupName(
                group as any as IBidderGroup,
                user.user.Username,
                cfg.table.columns.Name.responseKey,
              );
            }
            let count = 0;
            group.BidderIDs.split(',').forEach((id) => {
              if (biddersList[id]) {
                count++;
              }
            });
            group.BidderCount = count;
          });
          unstable_batchedUpdates(() => {
            setResponseData(bidderGroups);
            setLoadState(isEmpty(bidderGroups) ? LoadingStateNoData : LoadingStateHasData);
          });
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setResponseData([]);
            setLoadState(LoadingStateNoData);
            setSnackbarMessage('Error retrieving data.');
            setSnackbarError(true);
          });
        });
    }
  };

  const getBidders = () => {
    getBiddersList(session, user).then((result) => {
      setBiddersList(result.Bidders);
    });
  };

  const patchBidderGroups = (updatedGroups: IBidderGroup[]) => {
    setLoadState(LoadingStateLoading);
    updateBidderGroups({
      query: {
        publisher_id: session.publisher.ID,
        bidder_groups: updatedGroups.map((group: IBidderGroup) => {
          group.BidderIDs = group.BidderIDs.split(',')
            .filter((id: string) => id !== 'Select All')
            .join(',');
          return group;
        }),
      },
    })
      .then(({ bidderGroups }: { bidderGroups: IBidderGroup[] }) => {
        bidderGroups.forEach((group) => {
          // obfuscate if needed
          if (isDemoUser) {
            group.Name = obfuscateBidderGroupName(
              group as any as IBidderGroup,
              user.user.Username,
              cfg.table.columns.Name.responseKey,
            );
          }
        });
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Successfully updated bidder groups.');
          setSnackbarError(false);
          setResponseData(bidderGroups);
        });
        handleCloseModals();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage('Updating bidder group settings failed.');
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  // handles

  const handleClickOnName = (id: string) => {
    const clonedData = cloneDeep(responseData);
    const selectedGroup = clonedData.find(({ ID }) => ID === id);
    setActiveGroup(selectedGroup || ({} as IBidderGroup));
    setActiveModal(ESelectedModal.GroupEditor);
  };

  const handleClickOnGroupedBidders = (id: string) => {
    const clonedData = cloneDeep(responseData);
    const selectedGroup = clonedData.find(({ ID }) => ID === id);
    setActiveGroup(selectedGroup || ({} as IBidderGroup));
    setActiveModal(ESelectedModal.BiddersList);
  };

  const handleChangeSelectedBidderGroupLabels = (labels: IBidderGroupLabel[]) => {
    // map any string IDs to integers before updating state
    const mappedBidderGroupLabels = labels.map((label) => {
      if (typeof label.ID === 'string') {
        return Object.assign(label, { ID: parseInt(label.ID, 10) });
      } else {
        return label;
      }
    });
    setSelectedBidderGroupLabels(mappedBidderGroupLabels);
  };

  const handleChangeBidderGroup = (name: string, bidderIDs: string, bidderGroupID: number) => {
    const clonedBidderGroups = cloneDeep(tempGroups);
    const selectedGroup = clonedBidderGroups.find((group) => group.ID === bidderGroupID.toString());
    if (selectedGroup) {
      (selectedGroup.Name = name),
        (selectedGroup.BidderIDs = bidderIDs),
        (selectedGroup.BidderGroupLabels = selectedBidderGroupLabels);
      // error checks
      if (!selectedGroup.Name) {
        unstable_batchedUpdates(() => {
          setSnackbarError(true);
          setSnackbarMessage('Bidder Group must have a name.');
        });
        return;
      }
      if (!selectedGroup.BidderIDs) {
        unstable_batchedUpdates(() => {
          setSnackbarError(true);
          setSnackbarMessage('Bidder Group must have bidders selected.');
        });
        return;
      }
    }
    setTempGroups(clonedBidderGroups);
    patchBidderGroups(clonedBidderGroups);
  };

  const handleAcceptBidderSelect = (list: IMenuList): string => {
    const bidderIDs: string[] = [];
    for (const { ID, selected } of Object.values(list)) {
      if (selected) {
        bidderIDs.push(ID);
      }
    }
    if (isEmpty(bidderIDs)) {
      unstable_batchedUpdates(() => {
        setSnackbarMessage('Bidder Group must have at least one member.');
        setSnackbarError(true);
      });
      return '';
    } else {
      const clonedBidderGroups = cloneDeep(tempGroups);
      for (let i = 0; i < clonedBidderGroups.length; i++) {
        if (activeGroup && clonedBidderGroups[i].ID === activeGroup.ID) {
          clonedBidderGroups[i].BidderIDs = bidderIDs.join(',');
          break;
        }
      }
      setTempGroups(clonedBidderGroups);
    }
    return bidderIDs.join(',');
  };

  const handleCloseModals = () => {
    unstable_batchedUpdates(() => {
      setActiveGroup(undefined);
      setActiveModal(undefined);
      setIsGroupSettingsModalActive(false);
      setIsBiddersListModalActive(false);
    });
  };

  const handleCreateNewGroup = () => {
    const clonedBidderGroups = cloneDeep(tempGroups);
    const newGroup = {
      ID: '0',
      Name: '',
      BidderIDs: '',
      BidderGroupLabels: [] as IBidderGroupLabel[],
    } as IBidderGroup;
    clonedBidderGroups.push(newGroup);
    unstable_batchedUpdates(() => {
      setActiveGroup(newGroup);
      setActiveModal(ESelectedModal.GroupEditor);
      setTempGroups(clonedBidderGroups);
    });
  };

  const handleCloneItem = (id: string) => {
    const clonedBidderGroups = cloneDeep(tempGroups);
    const selectedBidderGroup = clonedBidderGroups.find((group) => group.ID === id);
    if (selectedBidderGroup) {
      const newGroup = {
        ID: '0',
        Name: `${selectedBidderGroup.Name || ''} - Copy`,
        BidderIDs: selectedBidderGroup.BidderIDs || '',
        BidderGroupLabels: selectedBidderGroup.BidderGroupLabels || [],
      } as IBidderGroup;
      clonedBidderGroups.push(newGroup);
      unstable_batchedUpdates(() => {
        setActiveGroup(newGroup);
        setActiveModal(ESelectedModal.GroupEditor);
        setTempGroups(clonedBidderGroups);
      });
    }
  };

  const handleDeleteItem = (id: string) => {
    setIsTableActionPending(true);
    const clonedBidderGroups = cloneDeep(tempGroups);
    const filteredBidderGroups = clonedBidderGroups.filter((bidderGroup) => bidderGroup.ID !== id);
    patchBidderGroups(filteredBidderGroups);
    return Promise.resolve(undefined);
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
    cfg.defaultState.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
    cfg.defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const handleOnChangeQuery = (text: string) => {
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setQuery(text);
    });
  };

  const handleChangeActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  // convert the data to use the new table interface
  const rows = formatBidderGroupsResponseIntoLVTRows(queriedData, cfg.table);

  // since all data is collected, we handle the pagination here
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel>
        <Cols
          wrap
          gap={spacing.small}
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            rowGap: spacing.small,
          }}
        >
          {/* CREATE BIDDER GROUP BUTTON */}
          <Col>
            <ButtonV2
              onClick={handleCreateNewGroup}
              className={`cypress-${cypressClassName}-button-createsegment`}
              disabled={!writeAuthorization}
            >
              Create Bidder Group
            </ButtonV2>
          </Col>
        </Cols>
      </Panel>
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row style={{ padding: '8px 0px 16px' }}>
            <Cols
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleOnChangeQuery}
                  msDelay={300}
                  placeholder='Search by Name'
                  prefix='search'
                />
              </Col>
              <Col>
                <ColumnSelectorAndTableExpand
                  // simple multi select props
                  preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                  resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                  columnsList={extractVisibleColumns(cfg.table.columns, activeColumnKeys || [])}
                  columnsHandleOnChangeOption={handleChangeActiveColumns}
                  columnsHandleOnAccept={handleChangeActiveColumns}
                  // icon toggle props
                  isTableExpanded={isTableExpanded}
                  setIsTableExpanded={setIsTableExpanded}
                />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.table.columns}
              customFunctions={{
                handleClickOnName,
                handleClickOnGroupedBidders,
              }}
              isExpanded={isTableExpanded}
              enableActions
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleCloneItem={handleCloneItem}
              handleDeleteItem={handleDeleteItem}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isActionPending={isTableActionPending}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={visibleRows}
              rowsPerPage={rowsPerPage}
              totalRowCount={totalRowCount}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      {/* modals are a special case and can act as a panel */}
      <GroupSettingsDialog
        active={isGroupSettingsModalActive}
        groupInfo={activeGroup}
        list={filteredBiddersList}
        menuTitle='Bidders'
        onClose={handleCloseModals}
        onUpdate={(name: string, bidderIDs: string) =>
          handleChangeBidderGroup(name, bidderIDs, activeGroup ? Number(activeGroup.ID) : 0)
        }
        handleItemAcceptClick={handleAcceptBidderSelect}
        selectedGroupLabels={
          (activeGroup && activeGroup.BidderGroupLabels) || selectedBidderGroupLabels
        }
        loadingGroups={loadState !== LoadingStateLoading}
        allGroupLabels={allGroupLabels}
        handleChangeSelectedGroupLabels={handleChangeSelectedBidderGroupLabels}
        quickFilterOptions={activeQuickFilterOptions}
        showPrefixID={true}
      />
      {activeGroup && (
        <Dialog
          fullWidth
          maxWidth='sm'
          keepMounted
          open={isBiddersListModalActive}
          onClose={handleCloseModals}
        >
          <ModalSelectTable
            title='Bidders'
            singularTitle='Bidder'
            items={Object.values(biddersList).filter((b: IListItem) =>
              activeGroup.BidderIDs.split(',').includes(b.ID),
            )}
            onSelectRow={(bidderID: string) => {
              navigate(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
                b: bidderID,
              });
            }}
            interactive={true}
          />
        </Dialog>
      )}
      {/* snackbar is a special case and can act as a panel */}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
